import { detectImportFormat, normalizeImportColumn, prepareImportColumns, validateImportMapping, type ImportColumn, type ImportField } from "@easy-core/spreadsheet";
import {
  readSpreadsheetRows,
  resolveMountedBasename,
  spreadsheetCellToString,
  writeSpreadsheetBlob,
  type ApiError,
  type SpreadsheetRow,
} from "@easy-core";

export type ContactGroupSummary = {
  id: number;
  name: string;
  color: string;
};

export type ContactRecord = {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone: string;
  company: string;
  address: string;
  city: string;
  notes: string;
  source: string;
  has_bounced: boolean;
  bounced_at: string | null;
  is_unsubscribed: boolean;
  groups: ContactGroupSummary[];
  created_at: string | null;
  updated_at: string | null;
};

export type GroupMemberSummary = {
  id: number;
  full_name: string;
  email: string;
  company: string;
  phone: string;
};

export type GroupRecord = {
  id: number;
  name: string;
  description: string;
  color: string;
  contact_count: number;
  members: GroupMemberSummary[];
  created_at: string | null;
  updated_at: string | null;
};

export type ContactsMeta = {
  app_name: string;
  app_version: string;
  release_channel: string;
  app_id: string;
  database: Record<string, string>;
};

export type ContactFormState = {
  id: number | null;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company: string;
  address: string;
  city: string;
  notes: string;
  source: string;
  group_ids: number[];
};

export type GroupFormState = {
  id: number | null;
  name: string;
  description: string;
  color: string;
  member_ids: number[];
};

export type ContactsStatusTone = "neutral" | "success" | "error";

export type StatusState = {
  message: string;
  tone: ContactsStatusTone;
};

export type ContactsFilters = {
  search: string;
  group_id: "all" | number;
  source: "all" | string;
  city: "all" | string;
  email_quality: "all" | "duplicates" | "bounced" | "missing" | "unsubscribed";
};

export type GroupFilters = {
  search: string;
};

export type ImportSource = "csv" | "xlsx" | "vcf";

export type ContactImportPreviewEntry = {
  rowNumber: number;
  mode: "create" | "update";
  matchedContactId: number | null;
  form: ContactFormState;
  groups: string[];
  issues: string[];
};

export type ContactsImportPreview = {
  columns?: ImportColumn[];
  fileName: string;
  source: ImportSource;
  sheetName: string;
  entries: ContactImportPreviewEntry[];
  readyCount: number;
  issueCount: number;
  createCount: number;
  updateCount: number;
};

const EMAIL_REGEX = /^[A-Z0-9._%+\-']+@[A-Z0-9.\-]+\.[A-Z]{2,}$/i;

const IMPORT_COLUMN_ALIASES: Record<string, string[]> = {
  first_name: ["first_name", "firstname", "prenom", "given_name", "given"],
  last_name: ["last_name", "lastname", "nom", "family_name", "surname"],
  full_name: ["full_name", "name", "nom_complet", "display_name", "contact"],
  email: ["email", "mail", "email_address", "adresse_email"],
  phone: ["phone", "telephone", "tel", "mobile", "portable"],
  company: ["company", "organisation", "organization", "societe", "entreprise", "org"],
  address: ["address", "adresse", "street", "street_address", "adresse_postale", "address_line"],
  city: ["city", "ville", "town", "commune", "locality"],
  groups: ["groups", "groupes", "group", "groupe", "categories", "lists", "tags"],
  notes: ["notes", "note", "commentaires", "comments", "memo"],
};

export const importFields: ImportField[] = [
  { key: "first_name", label: "Prénom" },
  { key: "last_name", label: "Nom" },
  { key: "full_name", label: "Nom complet" },
  { key: "email", label: "Email", required: true },
  { key: "phone", label: "Téléphone" },
  { key: "company", label: "Entreprise" },
  { key: "address", label: "Adresse" },
  { key: "city", label: "Ville" },
  { key: "groups", label: "Groupes" },
  { key: "notes", label: "Notes" },
];

const IMPORT_TEMPLATE_HEADERS = ["first_name", "last_name", "email", "phone", "company", "address", "city", "groups", "notes"] as const;

const IMPORT_TEMPLATE_SAMPLE_ROW = [
  "Claire",
  "Martin",
  "claire.martin@example.com",
  "+33 6 01 02 03 04",
  "Maison des Arts",
  "12 rue des Fleurs",
  "Lyon",
  "Abonnes newsletter; VIP",
  "A rencontre lors du salon d'automne.",
] as const;

export function apiUrl(path: string) {
  const normalizedPath = `api/${String(path || "").replace(/^\/+/, "")}`;
  const basename = resolveMountedBasename("contacts");
  if (basename === "/") {
    return `/${normalizedPath}`;
  }
  return `${basename}/${normalizedPath}`.replace(/\/{2,}/g, "/");
}

export function formatContactName(contact: Pick<ContactRecord, "full_name" | "first_name" | "last_name" | "email">) {
  const fullName = String(contact.full_name || "").trim();
  if (fullName) {
    return fullName;
  }

  const firstName = String(contact.first_name || "").trim();
  const lastName = String(contact.last_name || "").trim();
  const joinedName = [firstName, lastName].filter(Boolean).join(" ");
  return joinedName || String(contact.email || "").trim() || "Contact sans nom";
}

export function formatContactSource(source: string) {
  const normalized = String(source || "").trim().toLowerCase();
  if (normalized === "manual") {
    return "Manuel";
  }
  if (normalized === "csv") {
    return "Import CSV";
  }
  if (normalized === "xlsx" || normalized === "xls") {
    return "Import XLSX";
  }
  if (normalized === "vcf") {
    return "Import VCF";
  }
  if (!normalized) {
    return "Inconnu";
  }
  return normalized.toUpperCase();
}

export function sortContacts(contacts: ContactRecord[]) {
  return [...contacts].sort((left, right) => {
    const nameCompare = formatContactName(left).localeCompare(formatContactName(right), "fr", { sensitivity: "base" });
    if (nameCompare !== 0) {
      return nameCompare;
    }
    return left.email.localeCompare(right.email, "fr", { sensitivity: "base" });
  });
}

export function sortGroups(groups: GroupRecord[]) {
  return [...groups].sort((left, right) => left.name.localeCompare(right.name, "fr", { sensitivity: "base" }));
}

export function findDuplicateEmails(contacts: ContactRecord[]): Set<string> {
  const counts = new Map<string, number>();
  for (const contact of contacts) {
    const email = contact.email.trim().toLowerCase();
    if (!email) continue;
    counts.set(email, (counts.get(email) ?? 0) + 1);
  }
  const duplicates = new Set<string>();
  for (const [email, count] of counts) {
    if (count > 1) duplicates.add(email);
  }
  return duplicates;
}

export function createEmptyContactForm(): ContactFormState {
  return {
    id: null,
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    company: "",
    address: "",
    city: "",
    notes: "",
    source: "manual",
    group_ids: [],
  };
}

export function toContactForm(contact?: Partial<ContactRecord> | null): ContactFormState {
  return {
    id: typeof contact?.id === "number" ? contact.id : null,
    first_name: String(contact?.first_name || ""),
    last_name: String(contact?.last_name || ""),
    email: String(contact?.email || ""),
    phone: String(contact?.phone || ""),
    company: String(contact?.company || ""),
    address: String(contact?.address || ""),
    city: String(contact?.city || ""),
    notes: String(contact?.notes || ""),
    source: String(contact?.source || "manual"),
    group_ids: Array.isArray(contact?.groups) ? contact.groups.map((group) => group.id) : [],
  };
}

export function buildContactPayload(form: ContactFormState) {
  return {
    contact_id: form.id || undefined,
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    email: form.email.trim().toLowerCase(),
    phone: form.phone.trim(),
    company: form.company.trim(),
    address: form.address.trim(),
    city: form.city.trim(),
    notes: form.notes.trim(),
    source: form.source.trim() || "manual",
    group_ids: form.group_ids,
  };
}

export function createEmptyGroupForm(): GroupFormState {
  return {
    id: null,
    name: "",
    description: "",
    color: "#335cff",
    member_ids: [],
  };
}

export function toGroupForm(group?: Partial<GroupRecord> | null): GroupFormState {
  return {
    id: typeof group?.id === "number" ? group.id : null,
    name: String(group?.name || ""),
    description: String(group?.description || ""),
    color: String(group?.color || "#335cff"),
    member_ids: Array.isArray(group?.members) ? group.members.map((member) => member.id) : [],
  };
}

export function buildGroupPayload(form: GroupFormState) {
  return {
    group_id: form.id || undefined,
    name: form.name.trim(),
    description: form.description.trim(),
    color: normalizeGroupColor(form.color),
    member_ids: form.member_ids,
  };
}

export function createDefaultContactsFilters(): ContactsFilters {
  return {
    search: "",
    group_id: "all",
    source: "all",
    city: "all",
    email_quality: "all",
  };
}

export function createDefaultGroupFilters(): GroupFilters {
  return {
    search: "",
  };
}

export function contactMatchesFilters(contact: ContactRecord, filters: ContactsFilters, duplicateEmails?: Set<string>) {
  const normalizedSearch = filters.search.trim().toLowerCase();
  if (normalizedSearch) {
    const haystacks = [
      formatContactName(contact),
      contact.email,
      contact.phone,
      contact.company,
      contact.address,
      contact.city,
      contact.notes,
      ...contact.groups.map((group) => group.name),
    ].map((value) => String(value || "").toLowerCase());

    if (!haystacks.some((value) => value.includes(normalizedSearch))) {
      return false;
    }
  }

  if (filters.group_id !== "all" && !contact.groups.some((group) => group.id === filters.group_id)) {
    return false;
  }

  if (filters.source !== "all" && String(contact.source || "").toLowerCase() !== String(filters.source || "").toLowerCase()) {
    return false;
  }

  if (filters.city !== "all" && String(contact.city || "").trim().toLowerCase() !== String(filters.city || "").trim().toLowerCase()) {
    return false;
  }

  if (filters.email_quality === "duplicates") {
    const email = contact.email.trim().toLowerCase();
    if (!email || !duplicateEmails?.has(email)) {
      return false;
    }
  }

  if (filters.email_quality === "bounced" && !contact.has_bounced) {
    return false;
  }
  if (filters.email_quality === "missing" && contact.email.trim()) {
    return false;
  }
  if (filters.email_quality === "unsubscribed" && !contact.is_unsubscribed) {
    return false;
  }

  return true;
}

export function groupMatchesFilters(group: GroupRecord, filters: GroupFilters) {
  const normalizedSearch = filters.search.trim().toLowerCase();
  if (!normalizedSearch) {
    return true;
  }

  const haystacks = [
    group.name,
    group.description,
    ...group.members.map((member) => member.full_name),
    ...group.members.map((member) => member.email),
  ].map((value) => String(value || "").toLowerCase());

  return haystacks.some((value) => value.includes(normalizedSearch));
}

export function formatMemberSearch(contact: ContactRecord) {
  return [formatContactName(contact), contact.email, contact.company, contact.phone, contact.city]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function inferContactFieldErrors(message: string) {
  const content = (message || "").toLowerCase();
  if (content.includes("email")) {
    return ["email"];
  }
  if (content.includes("groupe")) {
    return ["group_ids"];
  }
  return [];
}

export function inferGroupFieldErrors(message: string) {
  const content = (message || "").toLowerCase();
  if (content.includes("nom du groupe") || content.includes("groupe avec ce nom")) {
    return ["name"];
  }
  if (content.includes("contact")) {
    return ["member_ids"];
  }
  return [];
}

export function normalizeApiError(error: unknown) {
  return error as ApiError;
}

export function resolveFieldErrors(error: unknown, fallback: string[] = []) {
  const apiError = error as ApiError;
  if (Array.isArray(apiError?.fieldErrors) && apiError.fieldErrors.length > 0) {
    return apiError.fieldErrors;
  }
  return fallback;
}

export function statusClassName(status: StatusState) {
  if (status.tone === "error") {
    return "status-line error";
  }
  if (status.tone === "success") {
    return "status-line success";
  }
  return "status-line";
}

export function hasFieldError(fieldErrors: string[], fieldId: string) {
  return fieldErrors.includes(fieldId);
}

export async function downloadImportTemplate(format: "csv" | "xlsx") {
  const templateRows = [Array.from(IMPORT_TEMPLATE_HEADERS), Array.from(IMPORT_TEMPLATE_SAMPLE_ROW)];

  if (format === "xlsx") {
    const blob = await writeSpreadsheetBlob(templateRows, "Contacts");
    triggerFileDownload(
      blob,
      "relyo-contacts-import-template.xlsx"
    );
    return;
  }

  const csvContent = templateRows.map((row) => row.map(escapeCsvCell).join(",")).join("\n");
  triggerFileDownload(
    new Blob([csvContent], { type: "text/csv;charset=utf-8" }),
    "relyo-contacts-import-template.csv"
  );
}

export async function parseImportFile(file: File, contacts: ContactRecord[], mapping?: string[]): Promise<ContactsImportPreview> {
  const format = await detectImportFormat(file);
  if (format === "vcf") {
    return parseVcfImportFile(file, contacts);
  }

  return parseSpreadsheetImportFile(file, contacts, mapping);
}

export function buildImportPayload(preview: ContactsImportPreview) {
  return {
    source: preview.source,
    entries: preview.entries
      .filter((entry) => entry.issues.length === 0)
      .map((entry) => ({
        first_name: entry.form.first_name,
        last_name: entry.form.last_name,
        full_name: buildPreviewFullName(entry.form),
        email: entry.form.email,
        phone: entry.form.phone,
        company: entry.form.company,
        address: entry.form.address,
        city: entry.form.city,
        notes: entry.form.notes,
        groups: entry.groups,
      })),
  };
}

async function parseSpreadsheetImportFile(file: File, contacts: ContactRecord[], mapping?: string[]): Promise<ContactsImportPreview> {
  const source = await detectImportFormat(file);
  const { sheetName, rows } = await readSpreadsheetRows(file);
  if (!sheetName || rows.length === 0) {
    throw new Error("Le fichier ne contient aucune feuille exploitable.");
  }

  if (rows.length < 2) {
    throw new Error("Le fichier doit contenir une ligne d'entete et au moins une ligne de donnees.");
  }

  const [headerRow, ...dataRows] = rows;
  const columns = prepareImportColumns(rows, resolveImportColumnKey, importFields);
  const headerMap = mapping || columns.map(column => column.target);
  if (mapping) {
    const errors = validateImportMapping(mapping, importFields);
    if (mapping.length !== headerRow.length || errors.length) throw new Error(errors.join(" ") || "Correspondances invalides.");
  }
  const byEmail = new Map(contacts.map((contact) => [contact.email.toLowerCase(), contact]));
  const entries = dataRows
    .map((row, index) => mapSpreadsheetImportRow(row, headerMap, byEmail, index + 2, source))
    .filter((entry) => Boolean(entry.form.email || entry.form.first_name || entry.form.last_name || entry.form.company));

  markDuplicatePreviewEntries(entries);

  const readyCount = entries.filter((entry) => entry.issues.length === 0).length;
  const updateCount = entries.filter((entry) => entry.mode === "update" && entry.issues.length === 0).length;
  const createCount = entries.filter((entry) => entry.mode === "create" && entry.issues.length === 0).length;

  return {
    columns: columns.map((column, index) => ({ ...column, target: headerMap[index] })),
    fileName: file.name,
    source,
    sheetName,
    entries,
    readyCount,
    issueCount: entries.length - readyCount,
    createCount,
    updateCount,
  };
}

async function parseVcfImportFile(file: File, contacts: ContactRecord[]): Promise<ContactsImportPreview> {
  const byEmail = new Map(contacts.map((contact) => [contact.email.toLowerCase(), contact]));
  const rawText = await file.text();
  const cards = extractVcards(rawText);
  if (cards.length === 0) {
    throw new Error("Aucune carte VCF exploitable n'a été détectée.");
  }

  const entries = cards
    .map((lines, index) => mapVcfCard(lines, byEmail, index + 1))
    .filter((entry) => Boolean(entry.form.email || entry.form.first_name || entry.form.last_name || entry.form.company));

  markDuplicatePreviewEntries(entries);

  const readyCount = entries.filter((entry) => entry.issues.length === 0).length;
  const updateCount = entries.filter((entry) => entry.mode === "update" && entry.issues.length === 0).length;
  const createCount = entries.filter((entry) => entry.mode === "create" && entry.issues.length === 0).length;

  return {
    fileName: file.name,
    source: "vcf",
    sheetName: "VCF",
    entries,
    readyCount,
    issueCount: entries.length - readyCount,
    createCount,
    updateCount,
  };
}

function mapSpreadsheetImportRow(
  row: SpreadsheetRow,
  headerMap: string[],
  byEmail: Map<string, ContactRecord>,
  rowNumber: number,
  source: ImportSource
): ContactImportPreviewEntry {
  const rawValues: Record<string, string> = {};
  headerMap.forEach((key, index) => {
    if (!key) {
      return;
    }
    rawValues[key] = normalizeImportedCell(row[index]);
  });

  const normalizedEmail = String(rawValues.email || "").trim().toLowerCase();
  const matchedContact = normalizedEmail ? byEmail.get(normalizedEmail) || null : null;
  const baseForm = matchedContact
    ? toContactForm(matchedContact)
    : {
        ...createEmptyContactForm(),
        source,
      };

  let firstName = String(rawValues.first_name || "").trim();
  let lastName = String(rawValues.last_name || "").trim();
  const fullName = String(rawValues.full_name || "").trim();
  if (fullName && (!firstName || !lastName)) {
    const [splitFirstName, splitLastName] = splitFullName(fullName);
    if (!firstName) {
      firstName = splitFirstName;
    }
    if (!lastName) {
      lastName = splitLastName;
    }
  }

  const groups = normalizeGroupTokens(rawValues.groups || "");
  const form: ContactFormState = {
    ...baseForm,
    id: matchedContact?.id || null,
    first_name: firstName || baseForm.first_name,
    last_name: lastName || baseForm.last_name,
    email: normalizedEmail || baseForm.email,
    phone: String(rawValues.phone || baseForm.phone || "").trim(),
    company: String(rawValues.company || baseForm.company || "").trim(),
    address: String(rawValues.address || baseForm.address || "").trim(),
    city: String(rawValues.city || baseForm.city || "").trim(),
    notes: String(rawValues.notes || baseForm.notes || "").trim(),
    source,
    group_ids: matchedContact ? matchedContact.groups.map((group) => group.id) : [],
  };

  return buildPreviewEntry(rowNumber, matchedContact, form, groups);
}

function mapVcfCard(lines: string[], byEmail: Map<string, ContactRecord>, rowNumber: number): ContactImportPreviewEntry {
  let firstName = "";
  let lastName = "";
  let fullName = "";
  let email = "";
  let phone = "";
  let company = "";
  let address = "";
  let city = "";
  let notes = "";
  let groups: string[] = [];

  lines.forEach((line) => {
    const separatorIndex = line.indexOf(":");
    if (separatorIndex <= 0) {
      return;
    }
    const rawKey = line.slice(0, separatorIndex);
    const rawValue = decodeVcardValue(line.slice(separatorIndex + 1));
    const key = rawKey.split(";")[0].trim().toUpperCase();

    if (key === "FN" && !fullName) {
      fullName = rawValue.trim();
      return;
    }
    if (key === "N") {
      const [familyName, givenName] = rawValue.split(";");
      if (!firstName) {
        firstName = String(givenName || "").trim();
      }
      if (!lastName) {
        lastName = String(familyName || "").trim();
      }
      return;
    }
    if (key === "EMAIL" && !email) {
      email = rawValue.trim().toLowerCase();
      return;
    }
    if (key === "TEL" && !phone) {
      phone = rawValue.trim();
      return;
    }
    if (key === "ORG" && !company) {
      company = rawValue.split(";").map((value) => value.trim()).filter(Boolean).join(" / ");
      return;
    }
    if (key === "ADR" && (!address || !city)) {
      const [postOfficeBox, extendedAddress, streetAddress, cityName, region, postalCode, country] = rawValue
        .split(";")
        .map((value) => value.trim());
      const streetParts = [postOfficeBox, extendedAddress, streetAddress].filter(Boolean);
      const trailingParts = [postalCode, region, country].filter(Boolean);
      if (!address) {
        address = [...streetParts, ...trailingParts].join(", ");
      }
      if (!city) {
        city = cityName || "";
      }
      return;
    }
    if (key === "NOTE" && !notes) {
      notes = rawValue.trim();
      return;
    }
    if (key === "CATEGORIES") {
      groups = normalizeGroupTokens(rawValue);
    }
  });

  if (fullName && (!firstName || !lastName)) {
    const [splitFirstName, splitLastName] = splitFullName(fullName);
    if (!firstName) {
      firstName = splitFirstName;
    }
    if (!lastName) {
      lastName = splitLastName;
    }
  }

  const matchedContact = email ? byEmail.get(email) || null : null;
  const baseForm = matchedContact ? toContactForm(matchedContact) : { ...createEmptyContactForm(), source: "vcf" };
  const form: ContactFormState = {
    ...baseForm,
    id: matchedContact?.id || null,
    first_name: firstName || baseForm.first_name,
    last_name: lastName || baseForm.last_name,
    email: email || baseForm.email,
    phone: phone || baseForm.phone,
    company: company || baseForm.company,
    address: address || baseForm.address,
    city: city || baseForm.city,
    notes: notes || baseForm.notes,
    source: "vcf",
    group_ids: matchedContact ? matchedContact.groups.map((group) => group.id) : [],
  };

  return buildPreviewEntry(rowNumber, matchedContact, form, groups);
}

function buildPreviewEntry(
  rowNumber: number,
  matchedContact: ContactRecord | null,
  form: ContactFormState,
  groups: string[]
): ContactImportPreviewEntry {
  const issues: string[] = [];

  if (!form.email.trim()) {
    issues.push("Email manquant.");
  } else if (!EMAIL_REGEX.test(form.email.trim())) {
    issues.push("Email invalide.");
  }

  return {
    rowNumber,
    mode: matchedContact ? "update" : "create",
    matchedContactId: matchedContact?.id || null,
    form,
    groups,
    issues,
  };
}

function markDuplicatePreviewEntries(entries: ContactImportPreviewEntry[]) {
  const rowsByEmail = new Map<string, number[]>();
  entries.forEach((entry) => {
    const key = entry.form.email.trim().toLowerCase();
    if (!key) {
      return;
    }
    rowsByEmail.set(key, [...(rowsByEmail.get(key) || []), entry.rowNumber]);
  });

  entries.forEach((entry) => {
    const key = entry.form.email.trim().toLowerCase();
    const duplicates = rowsByEmail.get(key) || [];
    if (duplicates.length > 1) {
      entry.issues.push(`Email en double dans le fichier (lignes ${duplicates.join(", ")}).`);
    }
  });
}

function resolveImportColumnKey(value: string | number | boolean | Date | null | undefined) {
  const cleanedValue = normalizeImportColumn(value);
  if (!cleanedValue) {
    return "";
  }
  const match = Object.entries(IMPORT_COLUMN_ALIASES).find(([, aliases]) => aliases.includes(cleanedValue));
  return match ? match[0] : cleanedValue;
}


function normalizeImportedCell(value: string | number | boolean | Date | null | undefined) {
  return spreadsheetCellToString(value);
}

function normalizeGroupTokens(value: string) {
  return Array.from(
    new Set(
      String(value || "")
        .split(/[\n,;|]+/)
        .map((token) => token.trim())
        .filter(Boolean)
    )
  );
}

function buildPreviewFullName(form: ContactFormState) {
  return [form.first_name.trim(), form.last_name.trim()].filter(Boolean).join(" ");
}

function splitFullName(value: string): [string, string] {
  const parts = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) {
    return ["", ""];
  }
  if (parts.length === 1) {
    return [parts[0], ""];
  }
  return [parts.slice(0, -1).join(" "), parts[parts.length - 1]];
}

function extractVcards(rawText: string) {
  const unfoldedLines = rawText
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .reduce<string[]>((lines, line) => {
      if ((line.startsWith(" ") || line.startsWith("\t")) && lines.length > 0) {
        lines[lines.length - 1] += line.slice(1);
        return lines;
      }
      lines.push(line);
      return lines;
    }, []);

  const cards: string[][] = [];
  let currentCard: string[] | null = null;

  unfoldedLines.forEach((line) => {
    const normalizedLine = line.trim();
    if (!normalizedLine) {
      return;
    }
    if (normalizedLine.toUpperCase() === "BEGIN:VCARD") {
      currentCard = [];
      return;
    }
    if (normalizedLine.toUpperCase() === "END:VCARD") {
      if (currentCard && currentCard.length > 0) {
        cards.push(currentCard);
      }
      currentCard = null;
      return;
    }
    if (currentCard) {
      currentCard.push(line);
    }
  });

  return cards;
}

function decodeVcardValue(value: string) {
  return String(value || "")
    .replace(/\\n/gi, "\n")
    .replace(/\\,/g, ",")
    .replace(/\\;/g, ";")
    .replace(/\\\\/g, "\\");
}

function normalizeGroupColor(value: string) {
  return /^#[0-9a-f]{6}$/i.test(String(value || "").trim()) ? String(value).trim() : "#335cff";
}

function escapeCsvCell(value: string) {
  const stringValue = String(value ?? "");
  if (/[",\n;]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

function triggerFileDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
